import { useCallback } from "react"
import API from "../../../utils/API"
import Sweetalert from "../../../utils/Sweetalert"
import { openAiTargetingModal } from "./AiTargetingModal"
import { openSchedulerModal } from "../Scheduler/SchedulerModal"

export function useAiTargetingAdActions({
  totemId,
  aiTargetingAd,
  isDefaultAdRow = false,
  totemHasDefaultAd = false,
  otherTargetingAds = [],
  maxDuration,
  onUpdate,
  onDelete,
  onStartDurationEdit,
  onDurationSubmitSuccess,
}) {
  const { ad, gender, ageRange, isDefault, planification, notVideoDuration } = aiTargetingAd

  const handleEditTargeting = useCallback(async () => {
    const existingTargetingAds = otherTargetingAds.map((a) => ({
      gender: a.gender,
      ageRange: a.ageRange || [],
    }))
    const config = await openAiTargetingModal({
      gender,
      ageRange: ageRange || [],
      isDefault,
      notVideoDuration: notVideoDuration || 0,
      adTypeId: ad?.adTypeId,
      defaultToggleDisabled: (isDefaultAdRow || totemHasDefaultAd) && !isDefault,
      isEditMode: true,
      existingTargetingAds,
    })

    if (config) {
      API.POST(
        `/totem-ai-targeting-ad/totem/${totemId}`,
        {
          adId: aiTargetingAd.adId,
          ...config,
          planification,
        },
        (response) => {
          Sweetalert.fireSuccessToast(response.data.message)
          onUpdate({ ...aiTargetingAd, ...config })
        }
      )
    }
  }, [
    totemId,
    aiTargetingAd,
    gender,
    ageRange,
    isDefault,
    planification,
    notVideoDuration,
    ad?.adTypeId,
    isDefaultAdRow,
    totemHasDefaultAd,
    otherTargetingAds,
    onUpdate,
  ])

  const handleDurationEdit = useCallback(() => {
    onStartDurationEdit?.()
  }, [onStartDurationEdit])

  const handleDurationSubmit = useCallback(
    (newNotVideoDuration) => {
      const value = typeof newNotVideoDuration === "number" ? newNotVideoDuration : parseInt(newNotVideoDuration, 10)

      if (maxDuration && ad.duration + value > maxDuration) {
        Sweetalert.fireWarningToast("Vous dépassez la limite de temps de diffusion pour une boucle")

        return
      }

      API.POST(
        `/totem-ai-targeting-ad/totem/${totemId}`,
        {
          adId: aiTargetingAd.adId,
          gender,
          ageRange,
          isDefault,
          notVideoDuration: value,
          planification,
        },
        (response) => {
          Sweetalert.fireSuccessToast(response.data.message)
          onUpdate({ ...aiTargetingAd, notVideoDuration: value })
          onDurationSubmitSuccess?.()
        }
      )
    },
    [totemId, aiTargetingAd, ad.duration, gender, ageRange, isDefault, planification, maxDuration, onUpdate, onDurationSubmitSuccess]
  )

  const handlePlanificationEdit = useCallback(async () => {
    const newPlanification = await openSchedulerModal(planification)

    if (newPlanification !== false) {
      API.POST(
        `/totem-ai-targeting-ad/totem/${totemId}`,
        {
          adId: aiTargetingAd.adId,
          gender,
          ageRange,
          isDefault,
          notVideoDuration,
          planification: newPlanification,
        },
        (response) => {
          Sweetalert.fireSuccessToast(response.data.message)
          onUpdate({ ...aiTargetingAd, planification: newPlanification })
        }
      )
    }
  }, [totemId, aiTargetingAd, gender, ageRange, isDefault, notVideoDuration, planification, onUpdate])

  const handleDelete = useCallback(() => {
    onDelete(aiTargetingAd.adId)
  }, [aiTargetingAd.adId, onDelete])

  return {
    handleEditTargeting,
    handleDurationEdit,
    handleDurationSubmit,
    handlePlanificationEdit,
    handleDelete,
  }
}
